import { useProfileSettings } from './ProfileSettingsContext';

export default function ProfileEditorForm() {
  const { editingDetails, setEditingDetails } = useProfileSettings();

  const updateField = (field: string, value: any) => {
    setEditingDetails(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-8">
      {/* 1. Source Documents (Moved to Top) */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Source Documents</h3>
        <p className="text-xs text-gray-500 mb-6 font-medium">
          These documents provide the source material for AI tailoring and semantic matching.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <UploadField 
            label="Master Resume" 
            fileName={editingDetails.defaultResumeName} 
            type="resume" 
          />
          <UploadField 
            label="Full CV" 
            fileName={editingDetails.defaultCvName} 
            type="cv" 
          />
          <UploadField 
            label="Typst Template" 
            fileName={editingDetails.defaultTypstTemplateName} 
            type="template" 
            accept=".typ"
          />
        </div>
      </section>

      {/* 2. Contact Info */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name" value={editingDetails.firstName} onChange={v => updateField('firstName', v)} placeholder="John" />
          <Field label="Last Name" value={editingDetails.lastName} onChange={v => updateField('lastName', v)} placeholder="Doe" />
          <Field label="Email" value={editingDetails.email} onChange={v => updateField('email', v)} placeholder="john@example.com" type="email" />
          <Field label="Phone" value={editingDetails.phone} onChange={v => updateField('phone', v)} placeholder="+1 (555) 000-0000" type="tel" />
        </div>
      </section>

      {/* 3. Professional Info */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Professional Presence</h3>
        <div className="space-y-4">
          <Field label="Target Job Title" value={editingDetails.currentJobTitle} onChange={v => updateField('currentJobTitle', v)} placeholder="Senior Software Engineer" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="LinkedIn" value={editingDetails.linkedinUrl} onChange={v => updateField('linkedinUrl', v)} placeholder="linkedin.com/in/username" />
            <Field label="GitHub" value={editingDetails.githubUrl} onChange={v => updateField('githubUrl', v)} placeholder="github.com/username" />
            <Field label="Portfolio" value={editingDetails.portfolioUrl} onChange={v => updateField('portfolioUrl', v)} placeholder="portfolio.com" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Professional Summary</label>
            <textarea
              value={editingDetails.summary}
              onChange={e => updateField('summary', e.target.value)}
              className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm"
              placeholder="Briefly describe your expertise and key achievements..."
            />
          </div>
        </div>
      </section>

      {/* 4. Address */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Location</h3>
        <div className="space-y-4">
          <Field label="Street Address" value={editingDetails.address} onChange={v => updateField('address', v)} placeholder="123 Main St" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="City" value={editingDetails.city} onChange={v => updateField('city', v)} />
            <Field label="State" value={editingDetails.state} onChange={v => updateField('state', v)} />
            <Field label="Zip" value={editingDetails.zipCode} onChange={v => updateField('zipCode', v)} />
            <Field label="Country" value={editingDetails.country} onChange={v => updateField('country', v)} />
          </div>
        </div>
      </section>

      {/* 5. Diversity & EEO */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">EEO & Identification</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select label="Gender" value={editingDetails.gender} onChange={v => updateField('gender', v)} options={[
            {value: 'male', label: 'Male'}, {value: 'female', label: 'Female'}, {value: 'non_binary', label: 'Non-binary'}, {value: 'decline', label: 'Decline to self-identify'}
          ]} />
          <Select label="Veteran Status" value={editingDetails.veteranStatus} onChange={v => updateField('veteranStatus', v)} options={[
            {value: 'not_veteran', label: 'Not a Veteran'}, {value: 'veteran', label: 'Veteran'}, {value: 'decline', label: 'Decline to self-identify'}
          ]} />
        </div>
      </section>
    </div>
  );
}

function UploadField({ label, fileName, type, accept = ".pdf" }: { label: string, fileName?: string, type: 'resume' | 'cv' | 'template', accept?: string }) {
  const { handleFileUpload, uploadingFile } = useProfileSettings();
  
  return (
    <div className={`p-4 rounded-xl border-2 border-dashed transition-all ${fileName ? 'border-green-500 bg-green-50/30' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
        {fileName && <span className="text-[9px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">Loaded</span>}
      </div>
      <input 
        type="file" 
        id={`${type}-upload-form`} 
        className="hidden" 
        onChange={e => e.target.files?.[0] && handleFileUpload(e.target.files[0], type)} 
        accept={accept} 
      />
      <label 
        htmlFor={`${type}-upload-form`} 
        className={`block group ${uploadingFile ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-blue-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <span className="truncate max-w-[120px]">
            {uploadingFile ? 'Uploading...' : (fileName || `Upload ${label}`)}
          </span>
        </div>
      </label>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }: { label: string, value: string, onChange: (v: string) => void, placeholder?: string, type?: string }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm focus:ring-2 focus:ring-blue-500 transition-all"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string, value: any, onChange: (v: any) => void, options: {value: string, label: string}[] }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">{label}</label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm"
      >
        <option value="">— Select —</option>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}
